import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  ElementType,
  forwardRef,
  JSX,
  ReactNode,
} from 'react';
import clsx from 'clsx';
import Spinner from '@components/Spinner';

type IntrinsicAnchorProps = DetailedHTMLProps<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  HTMLAnchorElement
>;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  as?: string | ElementType;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  href?: IntrinsicAnchorProps['href'];
  loading?: boolean;
  rel?: IntrinsicAnchorProps['rel'];
  target?: IntrinsicAnchorProps['target'];
  variant?: 'contained' | 'outlined' | 'text';
}

const Button = forwardRef(
  (
    {
      as = 'button',
      children,
      className = '',
      disabled = false,
      href,
      loading,
      variant = 'contained',
      ...props
    }: ButtonProps,
    ref
  ): JSX.Element => {
    let Component = as;

    if (Component != 'a' && href) {
      Component = 'a';
    }

    return (
      <Component
        className={clsx(
          {
            'inline-block': Component === 'a',
            'flex items-center justify-center': Component !== 'a',
          },
          variant === 'contained' && [
            'bg-blue-primary',
            'text-white',
            'border',
            'focus:ring-bg-primary/20 focus:ring-2',
            disabled ? 'border-transparent' : 'border-blue-primary',
          ],
          variant === 'outlined' && [
            'border border-blue-primary text-blue-primary',
            'focus:ring-bg-primary/20 focus:ring-2',
          ],
          variant === 'text' && ['text-blue-primary'],
          disabled && ['bg-opacity-50', 'border-opacity-50', 'text-opacity-50'],
          'rounded px-6 py-2 text-body1 outline-none ',
          className
        )}
        {...props}
        disabled={disabled}
        href={href}
        ref={ref}
      >
        {loading ? <Spinner className="my-0.5" /> : children}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export default Button;
